import { getRequest, postRequest } from 'config/Axios/baseConfig';

// TypeScript Interfaces
export interface Notification {
  id: number;
  recipient_email?: string;
  title: string;
  message: string;
  notification_type: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  metadata: Record<string, any>;
}

export interface NotificationListItem {
  id: number;
  title: string;
  message: string;
  notification_type: string;
  is_read: boolean;
  created_at: string;
  metadata: Record<string, any>;
}

export interface UnreadNotificationsResponse {
  data: NotificationListItem[];
  count: number;
}

export interface PaginatedNotificationsResponse {
  data: NotificationListItem[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface MarkReadResponse {
  message: string;
  data: Notification;
}

export interface MarkAllReadResponse {
  message: string;
  count: number;
}

/**
 * Notification Service
 * Handles all API calls for notification management
 */
export const notificationService = {
  /**
   * Get unread notifications for the authenticated user
   * GET /notifications/unread/
   */
  async getUnreadNotifications(): Promise<UnreadNotificationsResponse> {
    const response = await getRequest<UnreadNotificationsResponse>('notifications/unread/');
    return response.data;
  },

  /**
   * Get archived (read) notifications with pagination
   * GET /notifications/archived/?page=1&page_size=20
   */
  async getArchivedNotifications(
    page: number = 1,
    page_size: number = 20
  ): Promise<PaginatedNotificationsResponse> {
    const response = await getRequest<PaginatedNotificationsResponse>(
      `notifications/archived/?page=${page}&page_size=${page_size}`
    );
    return response.data;
  },

  /**
   * Mark a single notification as read
   * POST /notifications/{notification_id}/mark-read/
   */
  async markNotificationRead(notificationId: number): Promise<MarkReadResponse> {
    const response = await postRequest<MarkReadResponse>(
      `notifications/${notificationId}/mark-read/`,
      {}
    );
    return response.data;
  },

  /**
   * Mark all notifications as read
   * POST /notifications/mark-all-read/
   */
  async markAllNotificationsRead(): Promise<MarkAllReadResponse> {
    const response = await postRequest<MarkAllReadResponse>('notifications/mark-all-read/', {});
    return response.data;
  },
};
